import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin, useScript } from '#imports'

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const retry = async (fn, retries = MAX_RETRIES, delay = RETRY_DELAY) => {
  let lastError
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (e) {
      lastError = e
      if (i < retries - 1) {
        await sleep(delay * (i + 1))
      }
    }
  }
  throw lastError
}

export default defineNuxtPlugin((nuxtApp) => {
  const { bucketName, clientId } = useRuntimeConfig().public
  let endpoints = []
  let endpointsPromise = null
  let visibilityHandler = null

  // 使用 useScript 載入 FingerprintJS，bundle: true 會在建置時下載並打包腳本
  const fingerprintScript = useScript(
    {
      src: 'https://fpjscdn.net/v3/Z3A97s4eTA8uL5uWtVyN/iife.min.js',
    },
    { bundle: true, trigger: 'manual' }
  )

  const clearCache = () => {
    endpoints = []
    endpointsPromise = null
  }

  // 頁面隱藏時清除快取，回來時重新獲取最新資料
  if (typeof document !== 'undefined') {
    visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        clearCache()
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)
  }

  // 清理 event listener，避免記憶體洩漏
  nuxtApp.hooks.hook('app:unmount', () => {
    if (visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    clearCache()
  })

  const fetchEndpoints = async () => {
    if (endpointsPromise) {
      return endpointsPromise
    }

    if (!bucketName || !clientId) {
      endpoints = []
      return
    }

    endpointsPromise = (async () => {
      try {
        const url = `https://storage.googleapis.com/${bucketName}/site_resources/${clientId}/target/target.json`
        const response = await fetch(url)
        const { host: hosts } = await response.json()
        endpoints = [...new Set(hosts.map((host) => host.replace(/fnapi\d*/, 'fp')))]
      } catch (e) {
        console.error('Failed to fetch fingerprint endpoints:', e)
        endpoints = []
        endpointsPromise = null
      }
    })()

    return endpointsPromise
  }

  const getDeviceToken = async () => {
    try {
      await fetchEndpoints()
      await fingerprintScript.load()

      const fp = await retry(() =>
        window.FingerprintJS.load({
          endpoint: [...endpoints, window.FingerprintJS.defaultEndpoint],
        })
      )

      const result = await retry(() => fp.get({ timeout: 30000 }))
      return result.visitorId
    } catch (e) {
      console.error('Failed to get device token:', e)
      return null
    }
  }

  return {
    provide: {
      getDeviceToken,
    },
  }
})
